import { Button } from 'base/ui/button';
import { RedErrorText, useSubmit } from 'base/ui/form';
import { IconComment, IconCommentUnread } from 'base/ui/icons';
import { Inline, Rows, SpacerGrow } from 'base/ui/layout';
import { Textarea } from 'base/ui/textarea';
import { ToastQueue } from 'base/ui/toast';
import { TextSmallLight } from 'base/ui/typography';
import { AccessLevel } from 'blocks/blocks_service';
import { editorModeStore } from 'blocks/editor/editor_mode';
import { shortcut, useKeyboardShortcuts } from 'blocks/keyboard_shortcuts';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { SELECTION_HOVER_THROUGH_ATTR } from '../selection/frame';
import { globalSelectionsMeasurer } from '../selection/measurer';
import { idForSelectionState } from '../selection/states';
import styles from './adding.css';
import CommentCursor from './comment_cursor.icon.svg';
import { FloatingCommentPanel } from './floating_panel';
import { CommentPin } from './pin';
import { CommentingUiStore, getAttachmentsAtCursor } from './ui_store';

export const ToggleAddModeButton = observer(function _ToggleAddModeButton({
  store,
}: {
  store: CommentingUiStore;
}) {
  const toggle = React.useCallback(() => {
    if (!store.session.productRestrictions.commentsEnabled) {
      return store.session.showUpgradeWall?.('share');
    }
    store.addMode = !store.addMode;
  }, [store]);
  useKeyboardShortcuts(true, {
    [shortcut('c')]: toggle,
  });

  const isUnread = store.comments.allThreads.some(
    (t) => !t.resolved && !store.comments.isThreadRead(t.id),
  );

  return (
    <Button
      variant="secondary"
      background={store.addMode ? 'white' : 'grey3'}
      checked={store.addMode}
      icon={isUnread ? IconCommentUnread : IconComment}
      label={
        store.session.accessLevel === AccessLevel.COMMENTER
          ? 'Comment'
          : undefined
      }
      tooltip="Comment"
      tooltipShortcut="C"
      onClick={toggle}
    />
  );
});

function IconCommentWithUnreadDot() {
  return (
    <div className={styles.unreadDotContainer}>
      <IconComment />
      <div className={styles.unreadDot} />
    </div>
  );
}

let lastClosedPanelTime = 0;

export const ClickToAddComment = observer(function _ClickToAddComment({
  store,
  toastQueue,
}: {
  store: CommentingUiStore;
  toastQueue: ToastQueue;
}) {
  const cursorPos = React.useRef({ x: 0, y: 0 });
  const cursorRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      // Update directly - react feels laggier
      cursorRef.current?.style.setProperty('--x', `${e.clientX}px`);
      cursorRef.current?.style.setProperty('--y', `${e.clientY}px`);
      // We're always saving the position, so that if the overlay is enabled
      // via keyboard shortcut, the cursor will be in the right place.
      cursorPos.current.x = e.clientX;
      cursorPos.current.y = e.clientY;
    };
    window.addEventListener('mousemove', onMouseMove);
    return () => void window.removeEventListener('mousemove', onMouseMove);
  }, []);

  const onClickOverlay = React.useCallback(
    (e: React.MouseEvent) => {
      const now = +Date.now();
      if (lastClosedPanelTime > now - 250) {
        // What a hack.  Because the overlay code uses mousedown, it isn't clear how to
        // cancel the click event that'd be triggered.  So we do it time based :/
        return;
      }
      if (store.addThreadAt) {
        store.addThreadAt = undefined;
        return;
      }
      const attachTo = getAttachmentsAtCursor(e.clientX, e.clientY);
      if (!attachTo) {
        toastQueue.push({
          label: 'You can’t comment here. Try somewhere on the canvas instead',
        });
        return;
      }
      store.addThreadAt = {
        attachTo,
        originalClientX: e.clientX,
        originalClientY: e.clientY,
      };
    },
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [store],
  );

  if (!store.addMode || !store.canComment) {
    return null;
  }

  return (
    <div
      className={styles.overlay}
      onClick={onClickOverlay}
      {...{ [SELECTION_HOVER_THROUGH_ATTR]: true }}
    >
      <div
        className={styles.cursor}
        ref={cursorRef}
        style={{
          ['--x' as string]: cursorPos.current.x + 'px',
          ['--y' as string]: cursorPos.current.y + 'px',
        }}
      >
        <CommentCursor />
      </div>
    </div>
  );
});

export const AddThread = observer(function _AddThread({
  store,
}: {
  store: CommentingUiStore;
}) {
  const [pinElement, setPinElement] = useState<HTMLDivElement | null>(null);
  const onClose = React.useCallback(() => {
    store.addThreadAt = undefined;
    lastClosedPanelTime = +Date.now();
  }, [store]);

  if (!store.addThreadAt) return null;

  const { InlineAuthDialog } = store.session;
  if (InlineAuthDialog) {
    return (
      <InlineAuthDialog
        signupTitle="Create a free account to comment"
        onClose={onClose}
        isCommenter={true}
      />
    );
  }

  const { attachTo, originalClientX, originalClientY } = store.addThreadAt;
  const anchor = attachTo[0];
  if (!anchor) return null;
  const box = globalSelectionsMeasurer.get(
    'selectable',
    idForSelectionState(anchor.selection),
  ).rect;
  return (
    <>
      <CommentPin
        ref={setPinElement}
        avatars={[store.comments.myAvatar]}
        numReplies={0}
        isSelected={true}
        onClick={onClose}
        x={box?.x ? box.x + box.width * anchor.x : originalClientX}
        y={box?.y ? box.y + box.height * anchor.y : originalClientY}
      />
      <AddThreadPanel store={store} anchorTo={pinElement} onClose={onClose} />
    </>
  );
});

const AddThreadPanel = observer(function _AddThreadPanel({
  store,
  anchorTo,
  onClose,
}: {
  store: CommentingUiStore;
  anchorTo: HTMLElement | null;
  onClose: () => void;
}) {
  const [text, setText] = React.useState('');

  const [loading, error, handleSubmit] = useSubmit(async () => {
    if (!store.addThreadAt) return;
    if (!text) throw new Error('Please enter a comment');
    await store.comments.submitComment(text, {
      type: 'toplevel',
      ...store.addThreadAt,
      viewport: store.activeViewport,
    });
    store.session.analytics.track({
      event: 'project:comment_create',
      editor_mode: editorModeStore.get(),
      comment_type: 'toplevel',
      comment_viewport: store.activeViewport,
    });
    onClose();
  });

  const onKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.stopPropagation();
        e.preventDefault();
        handleSubmit();
      }
      if (e.key === 'Escape') {
        e.stopPropagation();
        e.preventDefault();
        onClose();
      }
    },
    [onClose, handleSubmit],
  );

  return (
    <FloatingCommentPanel anchorTo={anchorTo} onClose={onClose} store={store}>
      <Rows spacing={8}>
        <TextSmallLight tag="div">
          <div className={styles.textareaAsInputWrapper} onKeyDown={onKeyDown}>
            <Textarea
              ref={focusOnMount}
              variant="invisible"
              placeholder="Add a comment"
              value={text}
              onChange={setText}
              allowNewlines={true}
            />
          </div>
        </TextSmallLight>
        {error != null && <RedErrorText error={error} />}
        {text && (
          <Inline spacing={0}>
            <SpacerGrow />
            <Button
              variant="primary"
              label="Submit"
              type="submit"
              loading={loading}
              onClick={handleSubmit}
            />
          </Inline>
        )}
      </Rows>
    </FloatingCommentPanel>
  );
});

function focusOnMount(el: HTMLTextAreaElement | null) {
  el?.focus();
}
